import { useEffect, useMemo, useRef, useState } from 'react';
import { Icon } from './Icons.jsx';
import { TERMOS_USO_TEXT, POLITICA_PRIVACIDADE_TEXT } from '../content/legalContent.js';

// ─── mini-parser de markdown (só o suficiente para os textos legais) ────────

function parseBlocks(md) {
  const lines = md.split('\n');
  const blocks = [];
  let i = 0;
  const n = lines.length;

  while (i < n) {
    const line = lines[i];

    if (line.trim() === '') { i++; continue; }
    if (line.trim() === '---') { blocks.push({ type: 'hr' }); i++; continue; }
    if (/^### /.test(line)) { blocks.push({ type: 'h3', text: line.slice(4) }); i++; continue; }
    if (/^## /.test(line))  { blocks.push({ type: 'h2', text: line.slice(3) }); i++; continue; }
    if (/^# /.test(line))   { blocks.push({ type: 'h1', text: line.slice(2) }); i++; continue; }

    if (/^> /.test(line)) {
      const buf = [];
      while (i < n && /^>\s?/.test(lines[i])) { buf.push(lines[i].replace(/^>\s?/, '')); i++; }
      blocks.push({ type: 'quote', text: buf.join(' ') });
      continue;
    }

    if (/^\|/.test(line)) {
      const rows = [];
      while (i < n && /^\|/.test(lines[i])) { rows.push(lines[i]); i++; }
      const parseRow = (r) => r.replace(/^\||\|$/g, '').split('|').map((c) => c.trim());
      blocks.push({ type: 'table', header: parseRow(rows[0]), body: rows.slice(2).map(parseRow) });
      continue;
    }

    if (/^- /.test(line)) {
      const items = [];
      while (i < n && /^- /.test(lines[i])) { items.push(lines[i].slice(2)); i++; }
      blocks.push({ type: 'ul', items });
      continue;
    }

    if (/^[a-z]\) /.test(line)) {
      const items = [];
      while (i < n && /^[a-z]\) /.test(lines[i])) {
        const m = lines[i].match(/^([a-z])\) (.*)$/);
        items.push({ letter: m[1], text: m[2] });
        i++;
      }
      blocks.push({ type: 'ol', items });
      continue;
    }

    const buf = [line];
    i++;
    while (i < n && lines[i].trim() !== '' && !/^(#{1,3} |> ?|\||- |---$)/.test(lines[i]) && !/^[a-z]\) /.test(lines[i])) {
      buf.push(lines[i]);
      i++;
    }
    blocks.push({ type: 'p', text: buf.join(' ') });
  }

  return blocks;
}

function renderInline(text, keyBase, onLinkClick) {
  const parts = text.split(/(\*\*.+?\*\*|\*[^*].*?\*|\[.+?\]\(.+?\))/g).filter((p) => p !== '');
  return parts.map((part, idx) => {
    const key = `${keyBase}-${idx}`;
    if (/^\*\*.+\*\*$/.test(part)) {
      return <strong key={key}>{part.slice(2, -2)}</strong>;
    }
    if (/^\*[^*].*\*$/.test(part)) {
      return <em key={key}>{part.slice(1, -1)}</em>;
    }
    const linkMatch = part.match(/^\[(.+)\]\((.+)\)$/);
    if (linkMatch) {
      const [, label, href] = linkMatch;
      return (
        <a
          key={key}
          href="#"
          onClick={(e) => { e.preventDefault(); onLinkClick?.(href); }}
          style={{ color: 'var(--brand-primary)', fontWeight: 600, textDecoration: 'underline' }}
        >
          {label}
        </a>
      );
    }
    return part;
  });
}

function Block({ block, idx, onLinkClick }) {
  const key = `b${idx}`;
  switch (block.type) {
    case 'h1':
      return <h2 style={{ fontSize: 18, marginTop: idx === 0 ? 0 : 22, marginBottom: 4 }}>{renderInline(block.text, key, onLinkClick)}</h2>;
    case 'h2':
      return <h3 style={{ fontSize: 15.5, marginTop: 22, marginBottom: 8, color: 'var(--midnight)' }}>{renderInline(block.text, key, onLinkClick)}</h3>;
    case 'h3':
      return <h4 style={{ fontSize: 14, marginTop: 16, marginBottom: 6, color: 'var(--midnight)' }}>{renderInline(block.text, key, onLinkClick)}</h4>;
    case 'hr':
      return <hr style={{ border: 0, borderTop: '1px solid var(--border)', margin: '18px 0' }}/>;
    case 'quote':
      return (
        <div style={{
          background: 'var(--marble-soft)', borderLeft: '3px solid var(--marble)',
          borderRadius: 8, padding: '10px 12px', margin: '10px 0',
          fontSize: 13, color: 'var(--text-muted)', lineHeight: 1.5,
        }}>
          {renderInline(block.text, key, onLinkClick)}
        </div>
      );
    case 'ul':
      return (
        <ul style={{ margin: '8px 0', paddingLeft: 20, display: 'flex', flexDirection: 'column', gap: 6 }}>
          {block.items.map((it, i) => <li key={i} style={{ fontSize: 13.5, lineHeight: 1.55 }}>{renderInline(it, `${key}-${i}`, onLinkClick)}</li>)}
        </ul>
      );
    case 'ol':
      return (
        <ul style={{ listStyle: 'none', margin: '8px 0', padding: 0, display: 'flex', flexDirection: 'column', gap: 6 }}>
          {block.items.map((it, i) => (
            <li key={i} style={{ display: 'flex', gap: 8, fontSize: 13.5, lineHeight: 1.55 }}>
              <span style={{ fontWeight: 600, color: 'var(--text-subtle)', flexShrink: 0 }}>{it.letter})</span>
              <span>{renderInline(it.text, `${key}-${i}`, onLinkClick)}</span>
            </li>
          ))}
        </ul>
      );
    case 'table':
      return (
        <div style={{ overflowX: 'auto', margin: '10px 0', border: '1px solid var(--border)', borderRadius: 10 }}>
          <table style={{ borderCollapse: 'collapse', width: '100%', fontSize: 12.5 }}>
            <thead>
              <tr>
                {block.header.map((h, i) => (
                  <th key={i} style={{ textAlign: 'left', padding: '8px 10px', background: 'var(--marble-soft)', fontFamily: 'var(--font-display)', fontWeight: 600, color: 'var(--midnight)', whiteSpace: 'nowrap' }}>
                    {renderInline(h, `${key}-h${i}`, onLinkClick)}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {block.body.map((row, ri) => (
                <tr key={ri} style={{ borderTop: '1px solid var(--border)' }}>
                  {row.map((cell, ci) => (
                    <td key={ci} style={{ padding: '8px 10px', color: 'var(--text-muted)', verticalAlign: 'top' }}>
                      {renderInline(cell, `${key}-${ri}-${ci}`, onLinkClick)}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      );
    case 'p':
    default:
      return <p style={{ margin: '10px 0', fontSize: 13.5, lineHeight: 1.6, color: 'var(--text)' }}>{renderInline(block.text, key, onLinkClick)}</p>;
  }
}

function LegalMarkdown({ text, onLinkClick }) {
  const blocks = useMemo(() => parseBlocks(text), [text]);
  return <>{blocks.map((b, i) => <Block key={i} block={b} idx={i} onLinkClick={onLinkClick}/>)}</>;
}

// ─── LegalModal ───────────────────────────────────────────────────────────

const DOCS = {
  termos: { label: 'Termos de uso', text: TERMOS_USO_TEXT },
  privacidade: { label: 'Política de privacidade', text: POLITICA_PRIVACIDADE_TEXT },
};

// Modal (bottom sheet) para leitura completa dos Termos de Uso e da Política
// de Privacidade, com rolagem própria e alternância entre os dois documentos.
export default function LegalModal({ doc, onClose }) {
  const [activeDoc, setActiveDoc] = useState(doc in DOCS ? doc : 'termos');
  const bodyRef = useRef(null);

  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = prev;
      window.removeEventListener('keydown', onKey);
    };
  }, [onClose]);

  useEffect(() => {
    bodyRef.current?.scrollTo({ top: 0 });
  }, [activeDoc]);

  const current = DOCS[activeDoc] || DOCS.termos;

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 300 }}>
      <div onClick={onClose} style={{ position: 'absolute', inset: 0, background: 'var(--overlay-navy-45)' }}/>
      <div className="bottom-sheet" style={{
        background: 'var(--white)', borderRadius: '20px 20px 0 0', boxShadow: '0 -20px 50px var(--overlay-navy-25)',
        display: 'flex', flexDirection: 'column', height: '88vh', maxHeight: '88vh',
      }}>
        {/* header */}
        <div style={{ flexShrink: 0, padding: '18px 18px 0', background: 'var(--white)', borderRadius: '20px 20px 0 0' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <div style={{ fontFamily: 'var(--font-display)', fontWeight: 600, fontSize: 16, color: 'var(--midnight)', flex: 1 }}>
              Documentos legais
            </div>
            <button onClick={onClose} aria-label="Fechar" style={{ color: 'var(--text-muted)', padding: 4 }}>
              <Icon.x size={22}/>
            </button>
          </div>

          <div style={{ display: 'flex', gap: 6, marginTop: 14, background: 'var(--marble-soft)', borderRadius: 12, padding: 4 }}>
            {Object.entries(DOCS).map(([key, { label }]) => (
              <button
                key={key}
                onClick={() => setActiveDoc(key)}
                style={{
                  flex: 1, padding: '9px 8px', borderRadius: 9, textAlign: 'center',
                  fontSize: 12.5, fontWeight: 600, fontFamily: 'var(--font-display)',
                  color: activeDoc === key ? 'var(--white)' : 'var(--text-muted)',
                  background: activeDoc === key ? 'var(--brand-primary)' : 'transparent',
                  transition: 'all 0.15s',
                }}
              >
                {label}
              </button>
            ))}
          </div>
        </div>

        {/* corpo com rolagem própria — precisa de background explícito para o
            wheel/scroll funcionar de forma confiável no Chrome em toda a área */}
        <div
          ref={bodyRef}
          style={{
            flex: 1, overflowY: 'auto', WebkitOverflowScrolling: 'touch',
            background: 'var(--white)', padding: '16px 18px 28px',
          }}
        >
          <LegalMarkdown text={current.text} onLinkClick={(href) => {
            if (/privacidade/i.test(href)) setActiveDoc('privacidade');
            else if (/termos/i.test(href)) setActiveDoc('termos');
          }}/>
        </div>
      </div>
    </div>
  );
}
